import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import config from 'config';
import { useAuthContext } from 'modules/auth/context';
import * as Api from '../api';
import * as Types from '../types';

interface IProps {
  onNew?: (notification: Types.INotification) => void;
}

/** The API forces a socket closed when its access token has expired; this many reconnects are allowed per window. */
const MAX_TOKEN_RECONNECTS = 5;
const TOKEN_RECONNECT_WINDOW_MS = 5 * 60 * 1000;

/**
 * Pushes events into React Query so the bell stays in sync. Torn down on logout — no listeners survive.
 *
 * This socket is also how the platform counts who is online, so it must stay up
 * for as long as the user is signed in:
 * - It connects only once the profile has loaded, by which time the HTTP client has
 *   replaced an expired access token (a returning user's stored token is usually stale).
 * - The token is read on every (re)connect, never captured once.
 * - The API verifies the token only on connect and closes the socket when it has expired,
 *   and socket.io does not retry a close the server started. So we make one authenticated
 *   request (the HTTP client refreshes the token on a 401) and connect again, a bounded
 *   number of times.
 */
const useNotificationsSocket = ({ onNew }: IProps = {}) => {
  const { state } = useAuthContext();
  const queryClient = useQueryClient();
  const socketRef = useRef<Socket | null>(null);
  const onNewRef = useRef(onNew);

  useEffect(() => {
    onNewRef.current = onNew;
  }, [onNew]);

  useEffect(() => {
    if (!state.isAuthenticated || !state.isFetched) return undefined;
    if (!localStorage.getItem(config.api.access_token_key)) return undefined;

    const socket = io(`${config.api.base_url}/notifications`, {
      transports: ['websocket'],
      auth: (send) => send({ token: localStorage.getItem(config.api.access_token_key) ?? '' }),
      reconnection: true,
      reconnectionDelay: 1000,
    });
    socketRef.current = socket;

    const reconnects: number[] = [];
    socket.on('disconnect', (reason) => {
      if (reason !== 'io server disconnect') return;
      const now = Date.now();
      while (reconnects.length > 0 && now - reconnects[0] > TOKEN_RECONNECT_WINDOW_MS) reconnects.shift();
      if (reconnects.length >= MAX_TOKEN_RECONNECTS) return;
      reconnects.push(now);
      Api.UnreadCount()
        .catch(() => undefined)
        .finally(() => {
          if (socketRef.current === socket) socket.connect();
        });
    });

    socket.on('notification:new', (payload: Types.INotification) => {
      // Drop the new row into the cached list without a refetch so the
      // dropdown updates instantly.
      queryClient.setQueriesData<Types.IApi.List.Response | undefined>(
        { queryKey: ['notifications', 'list'] },
        (prev) => {
          if (!prev) return prev;
          return { ...prev, items: [payload, ...prev.items] };
        },
      );
      // The badge count gets its own push below; this just ensures the
      // dropdown count derived from the list also goes up.
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
      onNewRef.current?.(payload);
    });

    socket.on('notification:unread-count', ({ unreadCount }: { unreadCount: number }) => {
      queryClient.setQueryData(['notifications', 'unread-count'], unreadCount);
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [state.isAuthenticated, state.isFetched, queryClient]);
};

export default useNotificationsSocket;
